import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography,
  RadioGroup,
  FormControl,
  Radio,
  FormControlLabel,
  FormHelperText
} from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { editMedia2, mediasRef } from '../../api/firebase/firestore/media';
import { mediaUserShema } from '../../api/firebase/firestore/media/validation';
import { usersRef } from '../../api/firebase/firestore/user';
import { targetChoice } from '../../constants/CheckboxList';
import {
  black,
  blackText,
  blueGrayText,
  cardBorder,
  primarycolor,
  gray,
  primaryButtonBackground,
  primaryButton
} from '../../constants/Colors';
import {
  buttonFont,
  cardContentPadding,
  cardSize,
  formWideMb,
  mypageHeader,
  mypageHeaderTitle,
  textFontSize,
  textMb,
  titleFontSize,
  titleMb
} from '../../constants/Location';
import { publisherMediaRegisterEditSteps } from '../../constants/RegisterStep';
import { mediaTypeList, percentList } from '../../constants/Select';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

import CustomCheckbox from './CustomCheckbox';
import CustomLoading from './CustomLoading';
import CustomSelectField from './CustomSelectField';
import CustomSnackbar from './CustomSnackbar';
import CustomStepper from './CustomStepper';
import CustomTextfield from './CustomTextfield';

import type { MediaType } from '../../api/firebase/firestore/media/mediaType';

const styles = {
  autoSize: {
    '@media screen and (max-width: 480px)': {
      width: '148px'
    },
    width: '180px'
  },
  buttonFont,
  cardBorder,
  cardContentPadding,
  cardSize,
  circle: {
    bgcolor: '#DBDBE0',
    borderRadius: '50%',
    height: '64px',
    marginBottom: '10px',
    width: '64px'
  },
  column: {
    '@media screen and (max-width: 480px)': {
      columnGap: '15px'
    },
    columnGap: '39px'
  },
  flex: {
    display: 'flex'
  },
  formWideMb,
  grayPlaceholderText: {
    color: '#757575',
    fontFamily: 'Noto Sans JP,sans-serif',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '1.5',
    marginBottom: '10px'
  },
  isRequiredText: {
    color: '#BD0017',
    fontFamily: 'Noto Sans JP,sans-serif',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '1.5',
    margin: '0',
    marginLeft: '10px'
  },
  issueDescription: {
    color: blueGrayText,
    fontSize: '14px',
    marginBottom: '24px'
  },
  label: {
    fontSize: '12px'
  },
  mypageHeader,
  mypageHeaderTitle,
  nextButton: {
    '@media screen and (max-width: 480px)': {
      display: 'none'
    }
  },
  nextButtonSp: {
    '@media screen and (min-width: 481px)': {
      display: 'none'
    }
  },
  radiomarginLeft: {
    marginLeft: '5px'
  },
  required: {
    color: primarycolor,
    fontSize: '12px',
    marginLeft: '10px'
  },
  skipButton: {
    height: '36px',
    width: '140px'
  },
  submit: {
    marginBottom: 0,
    marginTop: '32px',
    padding: 0
  },
  submitButton: {
    '@media screen and (max-width: 960px)': {
      height: '42px'
    },
    height: '42px'
  },
  submitCol: {
    columnGap: '8px'
  },
  text: {
    blackText,
    fontSize: '14px'
    // marginBottom: '10px'
  },
  textFontSize,
  textMb,
  title: {
    color: blackText,
    fontSize: titleFontSize
  },
  titleMb,
  topMargin: {
    marginTop: '24px'
  },
  topMarginSm: {
    // marginTop: '16px'
  },
  undoButton: {
    '&:hover': {
      borderColor: gray,
      opacity: 0.7
    },
    border: `1px solid ${gray}`,
    color: gray,
    height: '42px',
    marginRight: '16px'
  },
  uploadButton: {
    '&:hover': {
      backgroundColor: black
    },
    backgroundColor: black,
    height: '36px',
    marginLeft: '11px',
    minWidth: '157px'
  }
};

export interface RegisterMediaUserType {
  target: boolean[] | null;
  targetKind: number | null;
  targetDetail: string | null;
  genderMale: number;
  genderFemale: number;
  m10: number;
  m20: number;
  m30: number;
  m40: number;
  m50: number;
  m60: number;
  m70: number;
  f10: number;
  f20: number;
  f30: number;
  f40: number;
  f50: number;
  f60: number;
  f70: number;
  device1: number;
  device2: number;
  device3: number;
  area1: number;
  area2: number;
  area3: number;
  area4: number;
  area5: number;
  area6: number;
  area7: number;
  area8: number;
  merit: string | '';
}

const MediaUserInfoEdit = (): React.ReactElement => {
  const { t } = useTranslation();
  const steps = publisherMediaRegisterEditSteps();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const { id } = useParams<{ id: string }>();
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');

  const [firstSubmit, setFirstSubmit] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [mediaCompanyId, setMediaCompanyId] = useState<string>('');

  useEffect(() => {
    if (!currentUser) {
      setOpen(true);
      setErrorText(t('form.loginError'));
      setTimeout(() => {
        navigate('/auth/login');
      }, 6000);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    void (async () => {
      if (mediaCompanyId) {
        const userRef = doc(usersRef, currentUser?.uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();
        if (userData) {
          const userCompany = userData.companyId as string;
          if (userCompany !== mediaCompanyId) {
            navigate('/auth/login');
          }
        }
      }
    })();
  }, [mediaCompanyId]);

  const {
    control,
    watch,
    register,
    reset,
    handleSubmit,
    formState: { errors }
  } = useForm<RegisterMediaUserType>({
    defaultValues: {
      area1: 0,
      area2: 0,
      area3: 0,
      area4: 0,
      area5: 0,
      area6: 0,
      area7: 0,
      area8: 0,
      device1: 0,
      device2: 0,
      device3: 0,
      f10: 0,
      f20: 0,
      f30: 0,
      f40: 0,
      f50: 0,
      f60: 0,
      f70: 0,
      genderFemale: 0,
      genderMale: 0,
      m10: 0,
      m20: 0,
      m30: 0,
      m40: 0,
      m50: 0,
      m60: 0,
      m70: 0,
      merit: '',
      target: null,
      targetDetail: null,
      targetKind: null
    },
    resolver: yupResolver(mediaUserShema)
  });

  const createCheckboxBooleanArray = (
    data?: number[],
    length?: number
  ): boolean[] => {
    if (!length || !data) return [];

    const newArray = Array(length).fill(false) as boolean[];
    data.forEach((element: number) => {
      newArray[element] = true;
    });

    return newArray;
  };

  const [demograRequired, setDemograRequired] = useState<boolean>(true);
  const mTypeList = mediaTypeList();

  useEffect(() => {
    void (async () => {
      const mediaRef = doc(mediasRef, id);
      const mediaDoc = await getDoc(mediaRef);
      const mediaData = mediaDoc.data() as MediaType;
      let mediaTypeName;
      for (const group of mTypeList) {
        for (const option of group.options) {
          if (option.value === mediaData?.mediaType) {
            mediaTypeName = group.name;
            if (
              mediaTypeName === t('media.mediaTypeOption.tv') ||
              mediaTypeName === t('media.mediaTypeOption.radio')
            ) {
              setDemograRequired(false);
            } else {
              setDemograRequired(true);
            }
          }
        }
      }
      setMediaCompanyId(mediaData?.companyId);
      reset({
        area1: mediaData?.demographicsArea?.hokkaidou,
        area2: mediaData?.demographicsArea?.touhoku,
        area3: mediaData?.demographicsArea?.kantou,
        area4: mediaData?.demographicsArea?.chubu,
        area5: mediaData?.demographicsArea?.kinki,
        area6: mediaData?.demographicsArea?.chugoku,
        area7: mediaData?.demographicsArea?.shikoku,
        area8: mediaData?.demographicsArea?.kyushu,
        device1: mediaData?.demographicsDevice?.pc,
        device2: mediaData?.demographicsDevice?.sp,
        device3: mediaData?.demographicsDevice?.tablet,
        f10: mediaData?.demographicsGeneration?.f10,
        f20: mediaData?.demographicsGeneration?.f20,
        f30: mediaData?.demographicsGeneration?.f30,
        f40: mediaData?.demographicsGeneration?.f40,
        f50: mediaData?.demographicsGeneration?.f50,
        f60: mediaData?.demographicsGeneration?.f60,
        f70: mediaData?.demographicsGeneration?.f70,
        genderFemale: mediaData?.demographicsGender?.female,
        genderMale: mediaData?.demographicsGender?.male,
        m10: mediaData?.demographicsGeneration?.m10,
        m20: mediaData?.demographicsGeneration?.m20,
        m30: mediaData?.demographicsGeneration?.m30,
        m40: mediaData?.demographicsGeneration?.m40,
        m50: mediaData?.demographicsGeneration?.m50,
        m60: mediaData?.demographicsGeneration?.m60,
        m70: mediaData?.demographicsGeneration?.m70,
        merit: mediaData?.value,
        target: createCheckboxBooleanArray(
          mediaData?.target,
          watch('target')?.length
        ),
        targetDetail: mediaData?.demographicsOther,
        targetKind: mediaData?.targetType
      });
    })();
  }, []);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('target') === null ||
        watch('targetKind') === null ||
        !watch('merit') === null ||
        Boolean(errors.target) ||
        Boolean(errors.targetKind) ||
        Boolean(errors.targetDetail) ||
        Boolean(errors.merit) ||
        Boolean(genderVaridation) ||
        Boolean(geneVaridation) ||
        Boolean(deviceVaridation) ||
        Boolean(areaVaridation)
      );
    }
    return false;
  };

  // 男女比のバリデーション
  const [genderVaridation, setGenderVaridation] = useState<boolean>(false);
  const [genderTextError, setGenderTextError] = useState<string>('');

  const totalGenderCheck = (): boolean => {
    const total = watch('genderMale') + watch('genderFemale');
    if (demograRequired && total === 0) {
      setGenderVaridation(true);
      setGenderTextError(t('validation.error.required'));
      return false;
    } else if (!demograRequired && total === 0) {
      setGenderVaridation(false);
      setGenderTextError('');
      return true;
    } else if (total > 100 || total < 100) {
      setGenderVaridation(true);
      setGenderTextError('合計が100%にならなければなりません');
      return false;
    }
    setGenderVaridation(false);
    setGenderTextError('');
    return true;
  };

  useEffect(() => {
    if (firstSubmit && demograRequired) {
      totalGenderCheck();
    } else {
      setGenderVaridation(false);
      setGenderTextError('');
    }
  }, [firstSubmit, watch('genderMale'), watch('genderFemale')]);

  // 世代のバリデーション
  const [geneVaridation, setGeneVaridation] = useState<boolean>(false);
  const [geneTextError, setGeneTextError] = useState<string>('');

  const totalGenerationCheck = (): boolean => {
    const total =
      watch('f10') +
      watch('f20') +
      watch('f30') +
      watch('f40') +
      watch('f50') +
      watch('f60') +
      watch('f70') +
      watch('m10') +
      watch('m20') +
      watch('m30') +
      watch('m40') +
      watch('m50') +
      watch('m60') +
      watch('m70');

    if (demograRequired && total === 0) {
      setGeneVaridation(true);
      setGeneTextError(t('validation.error.required'));
      return false;
    } else if (!demograRequired && total === 0) {
      setGeneVaridation(false);
      setGeneTextError('');
      return true;
    } else if (total > 100 || total < 100) {
      setGeneVaridation(true);
      setGeneTextError('合計が100%にならなければなりません');
      return false;
    }
    setGeneVaridation(false);
    setGeneTextError('');
    return true;
  };

  useEffect(() => {
    if (firstSubmit) {
      totalGenerationCheck();
    } else {
      setGeneVaridation(false);
      setGeneTextError('');
    }
  }, [
    watch('f10'),
    watch('f20'),
    watch('f30'),
    watch('f40'),
    watch('f50'),
    watch('f60'),
    watch('f70'),
    watch('m10'),
    watch('m20'),
    watch('m30'),
    watch('m40'),
    watch('m50'),
    watch('m60'),
    watch('m70'),
    firstSubmit
  ]);

  // 都道府県のバリデーション
  const [deviceVaridation, setDeviceVaridation] = useState<boolean>(false);
  const [deviceTextError, setDeviceTextError] = useState<string>('');

  const totalDeviceCheck = (): boolean => {
    const total = watch('device1') + watch('device2') + watch('device3');
    if (demograRequired && total === 0) {
      setDeviceVaridation(true);
      setDeviceTextError(t('validation.error.required'));
      return false;
    } else if (!demograRequired && total === 0) {
      setDeviceVaridation(false);
      setDeviceTextError('');
      return true;
    } else if (total > 100 || total < 100) {
      setDeviceVaridation(true);
      setDeviceTextError('合計が100%にならなければなりません');
      return false;
    }
    setDeviceVaridation(false);
    setDeviceTextError('');
    return true;
  };

  useEffect(() => {
    if (firstSubmit) {
      totalDeviceCheck();
    } else {
      setDeviceVaridation(false);
      setDeviceTextError('');
    }
  }, [watch('device1'), watch('device2'), watch('device3'), firstSubmit]);

  // 都道府県のバリデーション
  const [areaVaridation, setAreaVaridation] = useState<boolean>(false);
  const [areaTextError, setAreaTextError] = useState<string>('');

  const totalAreaCheck = (): boolean => {
    const total =
      watch('area1') +
      watch('area2') +
      watch('area3') +
      watch('area4') +
      watch('area5') +
      watch('area6') +
      watch('area7') +
      watch('area8');
    if (total === 0) {
      setAreaVaridation(true);
      setAreaTextError(t('validation.error.required'));
    } else if (total > 100 || total < 100) {
      setAreaVaridation(true);
      setAreaTextError('合計が100%にならなければなりません');
    } else {
      setAreaVaridation(false);
      setAreaTextError('');
    }
    return total === 100;
  };

  useEffect(() => {
    if (firstSubmit) {
      totalAreaCheck();
    } else {
      setAreaVaridation(false);
      setAreaTextError('');
    }
  }, [
    watch('area1'),
    watch('area2'),
    watch('area3'),
    watch('area4'),
    watch('area5'),
    watch('area6'),
    watch('area7'),
    watch('area8'),
    firstSubmit
  ]);

  const undoToIssue = async (): Promise<void> => {
    if (id) {
      setLoading(true);
      try {
        await editMedia2(watch, id);
        navigate(`/publisher/mypage/media/${id}/edit1`);
      } catch (e) {
        throwError(e);
      }
    }
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      if (id) {
        await editMedia2(watch, id);
        navigate(`/publisher/mypage/media/${id}/edit3`);
      }
    } catch (e) {
      throwError(e);
    }
  };

  const handleButtonClick = async (): Promise<void> => {
    setFirstSubmit(true);

    const genderCheck = totalGenderCheck();
    const generationCheck = totalGenerationCheck();
    const deviceCheck = totalDeviceCheck();
    const areaCheck = totalAreaCheck();

    await handleSubmit(async () => {
      if (genderCheck && generationCheck && deviceCheck && areaCheck) {
        await onSubmit();
      }
    })();
  };

  return (
    <>
      <Typography
        component="div"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'static',
          width: '100%'
        }}
      >
        <Typography sx={styles.mypageHeader} component="div">
          <Typography sx={styles.mypageHeaderTitle}>
            {t('mypage.mediaRegister')}
          </Typography>
          <Button
            disabled={buttonDisabledFlag()}
            variant="contained"
            sx={[styles.nextButton, primaryButton]}
            onClick={handleButtonClick}
          >
            {t('publisherMediaRegisterEdit.toService')}
          </Button>
          <Button
            disabled={buttonDisabledFlag()}
            variant="contained"
            sx={[styles.nextButtonSp, primaryButton]}
            onClick={handleButtonClick}
          >
            {t('publisherMediaRegisterEdit.spButton')}
          </Button>
        </Typography>

        <CustomLoading loading={loading} />
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          sx={{ marginBottom: '24px', marginTop: '24px' }}
        >
          <Grid>
            <Card sx={[styles.cardSize, styles.cardBorder]}>
              <CardContent sx={styles.cardContentPadding}>
                <CustomStepper steps={steps} activeStep={1} />
                <Typography
                  component="div"
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '24px'
                  }}
                >
                  <Typography sx={styles.title} component="div">
                    {t('publisherMediaRegisterEdit.step2')}
                  </Typography>
                </Typography>

                <CustomCheckbox
                  labelText={t('publisherMediaRegisterEdit.targetLabel')}
                  isRequired
                  requiredText={t('form.required')}
                  error={Boolean(errors.target)}
                  helperText={errors.target?.message}
                  list={targetChoice()}
                  formMb="narrow"
                  control={control}
                  name="target"
                  registerMediaUser={register}
                  watchMediaUser={watch}
                  guideText={t('guideText.registerMedia.target')}
                />

                <Controller
                  render={({ field }) => (
                    <Typography align="left" component="div">
                      <Typography
                        component="div"
                        sx={[styles.flex, styles.topMargin]}
                      >
                        <Typography sx={styles.grayPlaceholderText}>
                          {t('media.targetType')}
                        </Typography>
                        <Typography sx={styles.isRequiredText}>
                          {t('form.required')}
                        </Typography>
                      </Typography>
                      <FormControl>
                        <RadioGroup
                          {...field}
                          aria-labelledby="demo-form-control-label-placement"
                          row
                          sx={{
                            '&.MuiFormGroup-root': {
                              alignContent: 'flex-start',
                              marginBottom: '10px'
                            }
                          }}
                        >
                          <FormControlLabel
                            value={1}
                            control={<Radio sx={{ color: gray }} />}
                            label={t('media.targetTypeOption.1')}
                            sx={blackText}
                          />
                          <FormControlLabel
                            value={2}
                            control={<Radio sx={{ color: gray }} />}
                            label={t('media.targetTypeOption.2')}
                            sx={blackText}
                          />
                          <FormControlLabel
                            value={3}
                            control={<Radio sx={{ color: gray }} />}
                            label={t('media.targetTypeOption.3')}
                            sx={blackText}
                          />
                        </RadioGroup>
                      </FormControl>
                      <FormHelperText
                        sx={{ primarycolor }}
                        error={Boolean(errors.targetKind)}
                      >
                        {errors.targetKind?.message}
                      </FormHelperText>
                    </Typography>
                  )}
                  control={control}
                  name="targetKind"
                />

                <Typography component="div" sx={styles.topMargin}>
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        isRequired={false}
                        labelText={t('media.targetDetail')}
                        multiline
                        rows={3}
                        fieldType="textareaRow3"
                        width="article"
                        error={Boolean(errors.targetDetail)}
                        helperText={errors.targetDetail?.message}
                        guideText={t('guideText.registerMedia.targetDetail')}
                      />
                    )}
                    control={control}
                    name="targetDetail"
                  />
                </Typography>
                <Typography component="div" sx={{ display: 'flex' }}>
                  <Typography sx={styles.grayPlaceholderText}>
                    {t('media.demographicInfo')}
                  </Typography>
                </Typography>
                <Typography
                  component="div"
                  sx={{ alignItems: 'center', display: 'flex', mt: '16px' }}
                >
                  <Typography sx={[styles.text, styles.topMarginSm]}>
                    {t('media.demoTable.genderPercent')}
                  </Typography>
                  {demograRequired && (
                    <Typography sx={styles.isRequiredText}>
                      {t('form.required')}
                    </Typography>
                  )}
                </Typography>
                <Typography sx={[styles.grayPlaceholderText, { mt: '4px' }]}>
                  {t('publisherMediaRegisterEdit.demograInfoDetail')}
                </Typography>
                <FormHelperText sx={{ primarycolor }} error={genderVaridation}>
                  {genderTextError}
                </FormHelperText>
                <Grid container sx={styles.column}>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.demoTable.male')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                        />
                      )}
                      control={control}
                      name="genderMale"
                    />
                  </Grid>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.demoTable.female')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                        />
                      )}
                      control={control}
                      name="genderFemale"
                    />
                  </Grid>
                </Grid>

                <Typography
                  component="div"
                  sx={{ alignItems: 'center', display: 'flex', mt: '16px' }}
                >
                  <Typography sx={[styles.text, styles.topMarginSm]}>
                    {t('media.generation')}
                  </Typography>
                  {demograRequired && (
                    <Typography sx={styles.isRequiredText}>
                      {t('form.required')}
                    </Typography>
                  )}
                </Typography>
                <Typography sx={[styles.grayPlaceholderText, { mt: '4px' }]}>
                  {t('publisherMediaRegisterEdit.demograInfoGeneration')}
                </Typography>
                <FormHelperText sx={{ primarycolor }} error={geneVaridation}>
                  {geneTextError}
                </FormHelperText>
                <Grid container sx={styles.column}>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.geneOption.m10')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                        />
                      )}
                      control={control}
                      name="m10"
                    />
                  </Grid>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.geneOption.m20')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                        />
                      )}
                      control={control}
                      name="m20"
                    />
                  </Grid>
                </Grid>

                <Grid container sx={styles.column}>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.geneOption.m30')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                        />
                      )}
                      control={control}
                      name="m30"
                    />
                  </Grid>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.geneOption.m40')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="generation"
                        />
                      )}
                      control={control}
                      name="m40"
                    />
                  </Grid>
                </Grid>

                <Grid container sx={styles.column}>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.geneOption.m60')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="generation"
                        />
                      )}
                      control={control}
                      name="m60"
                    />
                  </Grid>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.geneOption.m50')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="generation"
                        />
                      )}
                      control={control}
                      name="m50"
                    />
                  </Grid>
                </Grid>

                <Grid container sx={styles.column}>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.geneOption.m70')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="generation"
                        />
                      )}
                      control={control}
                      name="m70"
                    />
                  </Grid>
                </Grid>

                <Grid container sx={styles.column}>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.geneOption.f10')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="generation"
                        />
                      )}
                      control={control}
                      name="f10"
                    />
                  </Grid>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.geneOption.f20')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="generation"
                        />
                      )}
                      control={control}
                      name="f20"
                    />
                  </Grid>
                </Grid>

                <Grid container sx={styles.column}>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.geneOption.f30')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="generation"
                        />
                      )}
                      control={control}
                      name="f30"
                    />
                  </Grid>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.geneOption.f40')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="generation"
                        />
                      )}
                      control={control}
                      name="f40"
                    />
                  </Grid>
                </Grid>

                <Grid container sx={styles.column}>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.geneOption.f50')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="generation"
                        />
                      )}
                      control={control}
                      name="f50"
                    />
                  </Grid>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.geneOption.f60')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="generation"
                        />
                      )}
                      control={control}
                      name="f60"
                    />
                  </Grid>
                </Grid>
                <Grid container sx={styles.column}>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.geneOption.f70')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="generation"
                        />
                      )}
                      control={control}
                      name="f70"
                    />
                  </Grid>
                </Grid>

                <Typography
                  component="div"
                  sx={{ alignItems: 'center', display: 'flex', mt: '16px' }}
                >
                  <Typography sx={[styles.text, styles.topMarginSm]}>
                    {t('media.device')}
                  </Typography>
                  {demograRequired && (
                    <Typography sx={styles.isRequiredText}>
                      {t('form.required')}
                    </Typography>
                  )}
                </Typography>
                <Typography sx={[styles.grayPlaceholderText, { mt: '4px' }]}>
                  {t('publisherMediaRegisterEdit.demograInfoDetail')}
                </Typography>
                <FormHelperText sx={{ primarycolor }} error={deviceVaridation}>
                  {deviceTextError}
                </FormHelperText>
                <Grid container sx={styles.column}>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.deviceOption.pc')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="device"
                        />
                      )}
                      control={control}
                      name="device1"
                    />
                  </Grid>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.deviceOption.mobile')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="device"
                        />
                      )}
                      control={control}
                      name="device2"
                    />
                  </Grid>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.deviceOption.tablet')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="device"
                        />
                      )}
                      control={control}
                      name="device3"
                    />
                  </Grid>
                </Grid>

                <Typography
                  component="div"
                  sx={{ alignItems: 'center', display: 'flex', mt: '16px' }}
                >
                  <Typography sx={[styles.text, styles.topMarginSm]}>
                    {t('media.accessArea')}
                  </Typography>
                  <Typography sx={styles.isRequiredText}>
                    {t('form.required')}
                  </Typography>
                </Typography>
                <Typography sx={[styles.grayPlaceholderText, { mt: '4px' }]}>
                  {t('publisherMediaRegisterEdit.demograInfoDetail')}
                </Typography>
                <FormHelperText sx={{ primarycolor }} error={areaVaridation}>
                  {areaTextError}
                </FormHelperText>
                <Grid container sx={styles.column}>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.accessAreaOption.hokkaido')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="area"
                        />
                      )}
                      control={control}
                      name="area1"
                    />
                  </Grid>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.accessAreaOption.tohoku')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="area"
                        />
                      )}
                      control={control}
                      name="area2"
                    />
                  </Grid>
                </Grid>

                <Grid container sx={styles.column}>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.accessAreaOption.kanto')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="area"
                        />
                      )}
                      control={control}
                      name="area3"
                    />
                  </Grid>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.accessAreaOption.chubu')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="area"
                        />
                      )}
                      control={control}
                      name="area4"
                    />
                  </Grid>
                </Grid>

                <Grid container sx={styles.column}>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.accessAreaOption.kinki')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="area"
                        />
                      )}
                      control={control}
                      name="area5"
                    />
                  </Grid>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.accessAreaOption.chugoku')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="area"
                        />
                      )}
                      control={control}
                      name="area6"
                    />
                  </Grid>
                </Grid>

                <Grid container sx={styles.column}>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.accessAreaOption.shikoku')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="area"
                        />
                      )}
                      control={control}
                      name="area7"
                    />
                  </Grid>
                  <Grid item sx={styles.autoSize}>
                    <Controller
                      render={({ field }) => (
                        <CustomSelectField
                          {...field}
                          isRequired
                          labelText={t('media.accessAreaOption.kyushu')}
                          list={percentList()}
                          formMb="narrow"
                          width="search"
                          name="area"
                        />
                      )}
                      control={control}
                      name="area8"
                    />
                  </Grid>
                </Grid>

                <Typography component="div" sx={styles.topMargin}>
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        isRequired
                        requiredText={t('form.required')}
                        labelText={t('publisherMediaRegisterEdit.merit')}
                        multiline
                        rows={3}
                        fieldType={`textareaRow3`}
                        width="article"
                        error={Boolean(errors.merit)}
                        helperText={errors.merit?.message}
                        guideText={t('guideText.registerMedia.usersBenefit')}
                      />
                    )}
                    control={control}
                    name="merit"
                  />
                </Typography>

                <CardActions sx={[styles.textMb, styles.submit]}>
                  <Grid container sx={styles.submitCol}>
                    <Grid item>
                      <Button
                        disabled={buttonDisabledFlag()}
                        variant="outlined"
                        fullWidth
                        onClick={async () => {
                          setFirstSubmit(true);
                          await handleSubmit(async () => await undoToIssue())();
                        }}
                        sx={styles.undoButton}
                      >
                        <Typography sx={{ fontSize: '14px' }}>
                          {t('passwordReset.back')}
                        </Typography>
                      </Button>
                    </Grid>
                    <Grid item flex={1}>
                      <Button
                        disabled={buttonDisabledFlag()}
                        fullWidth
                        sx={[primaryButtonBackground, styles.submitButton]}
                        onClick={handleButtonClick}
                      >
                        <Typography
                          component="div"
                          color="white"
                          sx={{ fontSize: 15 }}
                        >
                          {t('publisherMediaRegisterEdit.toService')}
                        </Typography>
                      </Button>
                    </Grid>
                  </Grid>
                </CardActions>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <CustomSnackbar
          open={open}
          autoHideDuration={6000}
          onClose={() => setOpen(false)}
          text={errorText}
        />
      </Typography>
    </>
  );
};
export default MediaUserInfoEdit;
